import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

const KitPreview = ({ selectedKit, selectedColors, currentView, frontSvgData, backSvgData, svgLoading, category = 'football' }) => {
  const [modifiedFrontSvg, setModifiedFrontSvg] = useState(null);
  const [modifiedBackSvg, setModifiedBackSvg] = useState(null);
  const [isFullBodyDesign, setIsFullBodyDesign] = useState(true);

  // Function to detect if design is full body (has shorts/pants) or just shirt
  const checkIfFullBody = (svgContent) => {
    if (!svgContent) return true; // Default to full body
    
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgContent;
      const svgElement = tempDiv.querySelector('svg');
      
      if (!svgElement) return true;
      
      // Get viewBox or width/height
      const viewBox = svgElement.getAttribute('viewBox');
      const width = parseFloat(svgElement.getAttribute('width')) || 0;
      const height = parseFloat(svgElement.getAttribute('height')) || 0;
      
      let aspectRatio = 0;
      
      if (viewBox) {
        const parts = viewBox.split(' ');
        if (parts.length >= 4) {
          const vbWidth = parseFloat(parts[2]);
          const vbHeight = parseFloat(parts[3]);
          if (vbWidth > 0 && vbHeight > 0) {
            aspectRatio = vbHeight / vbWidth;
          }
        }
      } else if (width > 0 && height > 0) {
        aspectRatio = height / width;
      }
      
      // Full body designs typically have aspect ratio > 1.2 (taller than wide)
      // Shirt-only designs are usually closer to square or wider
      return aspectRatio > 1.2;
    } catch (error) {
      console.error('Error checking full body design:', error);
      return true; // Default to full body on error
    }
  };

  // PHASE 1: Analyze SVG to identify color groups WITHOUT applying colors
  const analyzeSvgColors = (svgContent, svgType = 'unknown') => {
    if (!svgContent) return null;
    
    // Create a temporary DOM element to parse the SVG (NOT rendered in DOM)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = svgContent;
    const svgElement = tempDiv.querySelector('svg');
    
    if (!svgElement) {
      return null;
    }
    
    // Helper function to normalize color values for comparison
    const normalizeColor = (color) => {
      if (!color || color === 'none' || color === 'transparent') return null;
      
      // Convert rgb() to hex, normalize hex values, etc.
      if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
          const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
          const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
          return `#${r}${g}${b}`.toLowerCase();
        }
      }
      
      // Normalize hex colors - expand 3-digit hex to 6-digit
      if (color.startsWith('#')) {
        const hex = color.toLowerCase();
        // Expand 3-digit hex (#fff) to 6-digit (#ffffff)
        if (hex.length === 4) {
          return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
        }
        return hex;
      }
      
      // Convert CSS color names to hex codes
      const colorNameMap = {
        'red': '#ff0000',
        'green': '#008000',
        'blue': '#0000ff',
        'white': '#ffffff',
        'black': '#000000',
        'yellow': '#ffff00',
        'cyan': '#00ffff',
        'magenta': '#ff00ff',
        'orange': '#ffa500',
        'pink': '#ffc0cb',
        'purple': '#800080',
        'brown': '#a52a2a',
        'grey': '#808080',
        'gray': '#808080',
        'silver': '#c0c0c0',
        'gold': '#ffd700',
        'navy': '#000080',
        'maroon': '#800000',
        'olive': '#808000',
        'lime': '#00ff00',
        'aqua': '#00ffff',
        'teal': '#008080',
        'fuchsia': '#ff00ff',
        'crimson': '#dc143c',
        'indigo': '#4b0082',
        'violet': '#ee82ee',
        'khaki': '#f0e68c',
        'salmon': '#fa8072',
        'tan': '#d2b48c',
        'beige': '#f5f5dc',
        'wheat': '#f5deb3',
        'chocolate': '#d2691e',
        'firebrick': '#b22222',
        'darkred': '#8b0000',
        'lightblue': '#add8e6',
        'lightgreen': '#90ee90',
        'lightgray': '#d3d3d3',
        'lightgrey': '#d3d3d3',
        'darkgray': '#a9a9a9',
        'darkgrey': '#a9a9a9',
        'darkblue': '#00008b',
        'darkgreen': '#006400',
        'darkorange': '#ff8c00',
        'darkviolet': '#9400d3',
        'lightyellow': '#ffffe0',
        'lightcyan': '#e0ffff',
        'lightpink': '#ffb6c1',
        'lightsalmon': '#ffa07a',
        'lightseagreen': '#20b2aa',
        'lightskyblue': '#87cefa',
        'lightsteelblue': '#b0c4de',
        'lightcoral': '#f08080',
        'palegreen': '#98fb98',
        'paleturquoise': '#afeeee',
        'palevioletred': '#db7093',
        'papayawhip': '#ffefd5',
        'peachpuff': '#ffdab9',
        'peru': '#cd853f',
        'plum': '#dda0dd',
        'powderblue': '#b0e0e6',
        'rosybrown': '#bc8f8f',
        'royalblue': '#4169e1',
        'saddlebrown': '#8b4513',
        'sandybrown': '#f4a460',
        'seagreen': '#2e8b57',
        'seashell': '#fff5ee',
        'sienna': '#a0522d',
        'skyblue': '#87ceeb',
        'slateblue': '#6a5acd',
        'slategray': '#708090',
        'slategrey': '#708090',
        'snow': '#fffafa',
        'springgreen': '#00ff7f',
        'steelblue': '#1e90ff',
        'thistle': '#d8bfd8',
        'tomato': '#ff6347',
        'turquoise': '#40e0d0',
        'violet': '#ee82ee',
        'wheat': '#f5deb3',
        'whitesmoke': '#f5f5f5',
        'yellowgreen': '#9acd32'
      };
      
      const normalizedName = color.toLowerCase().trim();
      if (colorNameMap[normalizedName]) {
        return colorNameMap[normalizedName];
      }
      
      return color.toLowerCase();
    };
    
    // Get style element to read CSS class definitions
    const styleElement = svgElement.querySelector('style');
    const styleContent = styleElement ? styleElement.textContent : '';
    
    // Helper function to get fill color from CSS class definition in style tag
    const getFillColorFromStyle = (className) => {
      if (!styleContent) return null;
      
      // Escape special regex characters in className
      const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      // First, try to match individual class definition: .className { fill: #color; }
      const classMatch = styleContent.match(
        new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*fill:\\s*([^;\\s}]+)`, 'i')
      );
      
      if (classMatch) {
        return normalizeColor(classMatch[1].trim());
      }
      
      // If not found, try to match grouped selectors: .cls-1, .cls-2, .className { fill: #color; }
      // This handles cases where multiple classes share the same fill color via grouped selector
      const groupedSelectorRegex = new RegExp(
        `([^\\n{]+(?:,\\s*[^\\n{]+)+)\\s*\\{[^}]*fill:\\s*([^;\\s}]+)`, 
        'gi'
      );
      
      let groupedMatch;
      while ((groupedMatch = groupedSelectorRegex.exec(styleContent)) !== null) {
        const selectorPart = groupedMatch[1];
        const fillColor = groupedMatch[2];
        
        // Check if this grouped selector contains our className
        // Split selectors by comma and check each one
        const selectors = selectorPart.split(',').map(s => s.trim());
        const hasOurClass = selectors.some(sel => {
          // Remove leading dot and whitespace, then compare
          const clsName = sel.replace(/^\./, '').trim();
          return clsName === className;
        });
        
        if (hasOurClass) {
          return normalizeColor(fillColor.trim());
        }
      }
      
      return null;
    };
    
    // Helper function to get fill color from inline style attribute
    const getFillColorFromInlineStyle = (element) => {
      const styleAttr = element.getAttribute('style');
      if (styleAttr && styleAttr.includes('fill:')) {
        const fillMatch = styleAttr.match(/fill:\s*([^;]+)/);
        if (fillMatch) {
          return normalizeColor(fillMatch[1].trim());
        }
      }
      return null;
    };
    
    // Helper function to get fill color from fill attribute
    const getFillColorFromAttribute = (element) => {
      const fillAttr = element.getAttribute('fill');
      if (fillAttr) {
        return normalizeColor(fillAttr);
      }
      return null;
    };
    
    // Helper function to check if a color is black (or very dark)
    const isBlackColor = (color) => {
      if (!color) return false;
      const normalized = normalizeColor(color);
      if (!normalized) return false;
      // Check for black variants: #000, #000000, #090909, rgb(0,0,0), etc.
      if (normalized === '#000000' || normalized === '#000' || normalized === '#090909') return true;
      // Check rgb values for very dark colors
      if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]);
          const g = parseInt(rgbMatch[2]);
          const b = parseInt(rgbMatch[3]);
          // Very dark colors (all values < 20)
          if (r < 20 && g < 20 && b < 20) return true;
        }
      }
      return false;
    };
    
    // Helper function to check if a color is white (or very light)
    const isWhiteColor = (color) => {
      if (!color) return false;
      const normalized = normalizeColor(color);
      if (!normalized) return false;
      // Check for white variants: #fff, #ffffff, rgb(255,255,255), etc.
      if (normalized === '#ffffff' || normalized === '#fff') return true;
      // Check rgb values for very light colors
      if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]);
          const g = parseInt(rgbMatch[2]);
          const b = parseInt(rgbMatch[3]);
          // Very light colors (all values > 240)
          if (r > 240 && g > 240 && b > 240) return true;
        }
      }
      return false;
    };
    
    // Analyze gradients and other effects in <defs>
    // Map: gradientId/patternId -> actual color (normalized)
    const effectColorMap = new Map();
    // Map: gradientId -> array of opacity values for each stop (to preserve opacity when applying colors)
    const gradientOpacityMap = new Map();
    
    const defsElement = svgElement.querySelector('defs');
    if (defsElement) {
      // Analyze gradients
      const gradients = defsElement.querySelectorAll('linearGradient, radialGradient');
      gradients.forEach(gradient => {
        const gradientId = gradient.getAttribute('id');
        const stops = gradient.querySelectorAll('stop');
        const stopColors = [];
        const opacityValues = [];
        
        stops.forEach(stop => {
          // Try stop-color attribute first
          let stopColor = stop.getAttribute('stop-color');
          let stopOpacity = stop.getAttribute('stop-opacity');
          
          // If not found, try style attribute
          if (!stopColor || stopOpacity === null) {
            const styleAttr = stop.getAttribute('style');
            if (styleAttr) {
              if (!stopColor) {
                const colorMatch = styleAttr.match(/stop-color:\s*([^;]+)/i);
                if (colorMatch) {
                  stopColor = colorMatch[1].trim();
                }
              }
              if (stopOpacity === null) {
                const opacityMatch = styleAttr.match(/stop-opacity:\s*([^;]+)/i);
                if (opacityMatch) {
                  stopOpacity = opacityMatch[1].trim();
                }
              }
            }
          }
          
          // For color grouping, we ignore opacity - same color with different opacities should be grouped together
          if (stopColor) {
            const normalized = normalizeColor(stopColor);
            if (normalized) {
              stopColors.push(normalized);
              // Store opacity for later use when applying colors (preserve original opacity)
              const opacity = stopOpacity !== null ? parseFloat(stopOpacity) : 1.0;
              opacityValues.push(opacity);
            }
          }
        });
        
        // Store opacity values for this gradient
        if (opacityValues.length > 0) {
          gradientOpacityMap.set(gradientId, opacityValues);
        }
        
        // Determine the dominant color of the gradient
        // Group by the actual color, regardless of what it is
        if (stopColors.length > 0) {
          // Count color occurrences to find the most common color
          const colorCounts = new Map();
          stopColors.forEach(color => {
            colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
          });
          
          // Find the most common color (dominant color)
          let dominantColor = null;
          let maxCount = 0;
          colorCounts.forEach((count, color) => {
            if (count > maxCount) {
              maxCount = count;
              dominantColor = color;
            }
          });
          
          // If all stops have the same color, use that
          // Otherwise use the most common color
          if (stopColors.length > 0 && stopColors.every(c => c === stopColors[0])) {
            dominantColor = stopColors[0];
          }
          
          if (dominantColor) {
            effectColorMap.set(gradientId, dominantColor);
          }
        }
      });
      
      // Analyze patterns (if any)
      const patterns = defsElement.querySelectorAll('pattern');
      patterns.forEach(pattern => {
        const patternId = pattern.getAttribute('id');
        // Try to find fill colors in pattern children
        const patternElements = pattern.querySelectorAll('[fill], [stop-color]');
        const patternColors = [];
        
        patternElements.forEach(el => {
          const fill = el.getAttribute('fill') || el.getAttribute('stop-color');
          if (fill && !fill.startsWith('url(')) {
            const normalized = normalizeColor(fill);
            if (normalized) {
              patternColors.push(normalized);
            }
          }
        });
        
        if (patternColors.length > 0) {
          // Use the most common color in the pattern
          const colorCounts = new Map();
          patternColors.forEach(color => {
            colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
          });
          
          let dominantColor = null;
          let maxCount = 0;
          colorCounts.forEach((count, color) => {
            if (count > maxCount) {
              maxCount = count;
              dominantColor = color;
            }
          });
          
              if (dominantColor) {
                effectColorMap.set(patternId, dominantColor);
              }
        }
      });
    }
    
    // Get all unique class names from the SVG
    const allClassNames = new Set();
    const allElements = svgElement.querySelectorAll('[class*="cls-"]');
    
    allElements.forEach(element => {
      const className = element.className.baseVal || element.className;
      if (className && typeof className === 'string') {
        // Handle multiple classes (space-separated)
        className.split(/\s+/).forEach(cls => {
          if (cls && cls.includes('cls-')) {
            allClassNames.add(cls);
          }
        });
      }
    });
    
    
    // Group classes by their original fill color (including gradients)
    const colorGroups = new Map(); // Map: fillColor -> array of classNames
    const gradientClassMap = new Map(); // Map: className -> gradientId (if uses gradient)
    const classOpacityMap = new Map(); // Map: className -> original opacity value (if it had opacity)
    
    // Helper function to get opacity from CSS class definition in style tag
    const getOpacityFromStyle = (className) => {
      if (!styleContent) return null;
      const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const opacityMatch = styleContent.match(
        new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*([^;\\s}]+)`, 'i')
      );
      if (opacityMatch) {
        const opacityValue = parseFloat(opacityMatch[1].trim());
        return isNaN(opacityValue) ? null : opacityValue;
      }
      return null;
    };
    
    // Analyze each class to determine its fill color and opacity
    allClassNames.forEach(className => {
      let fillColor = null;
      let gradientId = null;
      let originalOpacity = null;
      
      // Priority 1: Check CSS class definition in style tag
      fillColor = getFillColorFromStyle(className);
      originalOpacity = getOpacityFromStyle(className);
      
      // Check if it's a gradient URL
      if (fillColor && fillColor.startsWith('url(')) {
        const urlMatch = fillColor.match(/url\(#([^)]+)\)/);
        if (urlMatch) {
          gradientId = urlMatch[1];
          fillColor = null; // Will be handled separately
        }
      }
      
      // Priority 2: Check elements with this class for inline styles or fill attributes
      if (!fillColor && !gradientId) {
        const elementsWithClass = svgElement.querySelectorAll(`.${className}`);
        if (elementsWithClass.length > 0) {
          // Find an element that ONLY has this class (or check if opacity is specific to this class)
          // This prevents getting opacity from another class when an element has multiple classes
          let firstElement = null;
          let elementWithOnlyThisClass = null;
          
          for (const el of elementsWithClass) {
            const elClasses = (el.className.baseVal || el.className || '').split(/\s+/).filter(c => c);
            if (elClasses.length === 1 && elClasses[0] === className) {
              elementWithOnlyThisClass = el;
              break;
            }
            if (!firstElement) {
              firstElement = el;
            }
          }
          
          const elementToCheck = elementWithOnlyThisClass || firstElement;
          
          // Check for opacity in inline style if not found in CSS
          // Only use inline opacity if the element has ONLY this class, or if opacity is explicitly set on the element
          if (originalOpacity === null && elementToCheck) {
            // If element has only this class, we can safely use its opacity
            // Otherwise, we skip inline opacity to avoid getting opacity from another class
            const elClasses = (elementToCheck.className.baseVal || elementToCheck.className || '').split(/\s+/).filter(c => c);
            const hasOnlyThisClass = elClasses.length === 1 && elClasses[0] === className;
            
            if (hasOnlyThisClass) {
              const styleAttr = elementToCheck.getAttribute('style');
              if (styleAttr && styleAttr.includes('opacity:')) {
                const opacityMatch = styleAttr.match(/opacity:\s*([^;]+)/);
                if (opacityMatch) {
                  const opacityValue = parseFloat(opacityMatch[1].trim());
                  if (!isNaN(opacityValue)) {
                    originalOpacity = opacityValue;
                  }
                }
              }
              // Also check opacity attribute
              if (originalOpacity === null) {
                const opacityAttr = elementToCheck.getAttribute('opacity');
                if (opacityAttr) {
                  const opacityValue = parseFloat(opacityAttr);
                  if (!isNaN(opacityValue)) {
                    originalOpacity = opacityValue;
                  }
                }
              }
            }
          }
          
          // Use elementToCheck for fill color checks
          const firstElementForFill = elementToCheck || elementsWithClass[0];
          
          // Check for gradient URL in style
          const styleAttr = firstElementForFill.getAttribute('style');
        if (styleAttr && styleAttr.includes('fill:')) {
          const fillMatch = styleAttr.match(/fill:\s*([^;]+)/);
          if (fillMatch) {
              const fillValue = fillMatch[1].trim();
              if (fillValue.startsWith('url(')) {
                const urlMatch = fillValue.match(/url\(#([^)]+)\)/);
                if (urlMatch) {
                  gradientId = urlMatch[1];
                }
              } else {
                fillColor = normalizeColor(fillValue);
              }
            }
          }
          
          // Check fill attribute for gradient
          if (!fillColor && !gradientId) {
            const fillAttr = firstElementForFill.getAttribute('fill');
            if (fillAttr) {
              if (fillAttr.startsWith('url(')) {
                const urlMatch = fillAttr.match(/url\(#([^)]+)\)/);
                if (urlMatch) {
                  gradientId = urlMatch[1];
                }
              } else {
                fillColor = normalizeColor(fillAttr);
              }
            }
          }
        }
      }
      
      // Store opacity if it exists and is not 1.0 (default opacity)
      if (originalOpacity !== null && originalOpacity !== 1.0) {
        classOpacityMap.set(className, originalOpacity);
        // Debug logging for opacity detection
        if (svgType === 'BACK' && (className === 'cls-2' || className === 'cls-3')) {
          console.log(`[${svgType}] Detected opacity for ${className}: ${originalOpacity}`);
        }
      }
      
      // Handle gradient/pattern/effect classes
      if (gradientId) {
        gradientClassMap.set(className, gradientId);
        // Get the actual color from the effect (gradient, pattern, etc.)
        const effectColor = effectColorMap.get(gradientId);
        
        if (effectColor) {
          // Group by the actual color the effect produces
          if (!colorGroups.has(effectColor)) {
            colorGroups.set(effectColor, []);
          }
          if (!colorGroups.get(effectColor).includes(className)) {
            colorGroups.get(effectColor).push(className);
          }
        }
      }
      // Handle solid color classes
      else if (fillColor) {
        if (!colorGroups.has(fillColor)) {
          colorGroups.set(fillColor, []);
        }
        // Add className if not already in the array
        if (!colorGroups.get(fillColor).includes(className)) {
          colorGroups.get(fillColor).push(className);
        }
      }
    });
    
    // Convert Map to array and sort by frequency (most common colors first)
    const colorGroupsArray = Array.from(colorGroups.entries())
      .map(([color, classes]) => ({ color, classes, count: classes.length }))
      .sort((a, b) => b.count - a.count);
    
    
    return {
      svgElement,
      colorGroupsArray,
      styleElement,
      styleContent,
      effectColorMap,
      gradientClassMap,
      gradientOpacityMap,
      classOpacityMap // Map of className -> original opacity value
    };
  };
  
  // PHASE 2: Apply colors based on analysis results
  const applyColorsToSvg = (analysisResult, colors, svgType = 'unknown', category = 'football') => {
    if (!analysisResult) return null;
    
    
    const { svgElement, colorGroupsArray, styleElement, styleContent, effectColorMap, gradientClassMap, gradientOpacityMap, classOpacityMap } = analysisResult;
    
    // Remove white background if present
    svgElement.removeAttribute('background');
    svgElement.removeAttribute('bgcolor');
    svgElement.style.backgroundColor = 'transparent';
    
    // Remove any white background rectangles that cover the entire SVG
    const svgWidth = svgElement.getAttribute('width') || svgElement.getAttribute('viewBox')?.split(' ')[2] || '1000';
    const svgHeight = svgElement.getAttribute('height') || svgElement.getAttribute('viewBox')?.split(' ')[3] || '1000';
    
    const allRects = svgElement.querySelectorAll('rect');
    allRects.forEach(rect => {
      const rectWidth = parseFloat(rect.getAttribute('width') || '0');
      const rectHeight = parseFloat(rect.getAttribute('height') || '0');
      const rectX = parseFloat(rect.getAttribute('x') || '0');
      const rectY = parseFloat(rect.getAttribute('y') || '0');
      const fill = rect.getAttribute('fill') || '';
      
      // Check if this is a background rectangle (covers most of the SVG and is white/light)
      const coversMostOfSVG = rectWidth >= parseFloat(svgWidth) * 0.9 && rectHeight >= parseFloat(svgHeight) * 0.9;
      const isWhiteOrLight = fill === '#fff' || fill === '#ffffff' || fill === 'white' || 
                             fill === 'rgb(255, 255, 255)' || fill === 'rgb(255,255,255)' ||
                             !fill || fill === 'none';
      
      if (coversMostOfSVG && (isWhiteOrLight || rectX === 0 || rectY === 0)) {
        rect.remove();
      }
    });
    
    // Remove background from style element if present
    if (styleElement) {
      let updatedStyleContent = styleContent;
      // Remove background-related CSS rules
      updatedStyleContent = updatedStyleContent.replace(/background[^;]*;?\s*/gi, '');
      updatedStyleContent = updatedStyleContent.replace(/bgcolor[^;]*;?\s*/gi, '');
      styleElement.textContent = updatedStyleContent;
    }
    
    // Original SVG color codes (as specified)
    const ORIGINAL_COLOR1 = '#ffffff';  // White
    const ORIGINAL_COLOR2 = '#070707';  // Very dark gray/black
    const ORIGINAL_COLOR3 = '#ff0000';  // Red
    const ORIGINAL_LOGO_COLOR = '#ec0303';  // Logo red
    const ORIGINAL_NUMBER_COLOR = '#dfc200';  // Yellow/gold
    
    // Identify logo and number colors first (exclude them from main colors)
    const color4Classes = []; // Logo Color
    const color5Classes = []; // Number Color
    
    // Logo color patterns: #ec0303 (original logo color)
    const logoColorPatterns = [
      /^#ec0303$/i,  // Original logo color
      /^#ec\d{4}$/i,  // #ec0303 pattern (fallback)
    ];
    
    // Number color patterns: #dfc200 (original number color)
    const numberColorPatterns = [
      /^#dfc200$/i,  // Original number color
      /^#(df|ff)c[12]00$/i,  // #dfc200, #ffc200 (fallback)
      /^#ffc[0-9a-f]{3}$/i,  // Yellow variants
      /^#ffd[0-9a-f]{3}$/i,  // Light yellow variants
    ];
    
    colorGroupsArray.forEach(({ color, classes }) => {
      // Check if this is a logo color
      const isLogoColor = logoColorPatterns.some(pattern => pattern.test(color));
      // Check if this is a number color
      const isNumberColor = numberColorPatterns.some(pattern => pattern.test(color));
      
      if (isLogoColor) {
        color4Classes.push(...classes);
      }
      if (isNumberColor) {
        color5Classes.push(...classes);
      }
    });
    
    // Filter out logo and number colors from main color groups
    const mainColorGroups = colorGroupsArray.filter(({ color }) => {
      const isLogoColor = logoColorPatterns.some(pattern => pattern.test(color));
      const isNumberColor = numberColorPatterns.some(pattern => pattern.test(color));
      return !isLogoColor && !isNumberColor;
    });
    
    // Map original SVG colors to color1, color2, color3
    // Priority: Match exact original colors first, then fall back to frequency-based mapping
    let color1Classes = [];
    let color2Classes = [];
    let color3Classes = [];
    
    // Find original colors in the SVG
    const color1Group = mainColorGroups.find(({ color }) => color === ORIGINAL_COLOR1);
    const color2Group = mainColorGroups.find(({ color }) => color === ORIGINAL_COLOR2);
    const color3Group = mainColorGroups.find(({ color }) => color === ORIGINAL_COLOR3);
    
    // Debug logging for cricket category
    if (svgType === 'FRONT' && (mainColorGroups.some(g => g.color === '#ff0000' || g.color === 'red'))) {
      console.log(`[${svgType}] Color mapping debug:`);
      console.log('  mainColorGroups:', mainColorGroups.map(g => ({ color: g.color, classes: g.classes, count: g.count })));
      console.log('  color1Group:', color1Group ? { color: color1Group.color, classes: color1Group.classes } : 'NOT FOUND');
      console.log('  color2Group:', color2Group ? { color: color2Group.color, classes: color2Group.classes } : 'NOT FOUND');
      console.log('  color3Group:', color3Group ? { color: color3Group.color, classes: color3Group.classes } : 'NOT FOUND');
      console.log('  ORIGINAL_COLOR1:', ORIGINAL_COLOR1);
      console.log('  ORIGINAL_COLOR2:', ORIGINAL_COLOR2);
      console.log('  ORIGINAL_COLOR3:', ORIGINAL_COLOR3);
    }
    
    if (color1Group) {
      color1Classes = color1Group.classes;
    } else {
      // Fall back to most common color
      color1Classes = mainColorGroups[0]?.classes || [];
    }
    
    if (color2Group) {
      color2Classes = color2Group.classes;
    } else {
      // Fall back to second most common color
      color2Classes = mainColorGroups[1]?.classes || [];
    }
    
    if (color3Group) {
      color3Classes = color3Group.classes;
    } else {
      // Fall back to third most common color
      color3Classes = mainColorGroups[2]?.classes || [];
    }
    
    // Debug logging for cricket category - show final mapping
    if (svgType === 'FRONT' && (mainColorGroups.some(g => g.color === '#ff0000' || g.color === 'red'))) {
      console.log(`[${svgType}] Final color class mapping:`);
      console.log('  color1Classes:', color1Classes);
      console.log('  color2Classes:', color2Classes);
      console.log('  color3Classes:', color3Classes);
      console.log('  Selected colors:', {
        color1: colors.color1,
        color2: colors.color2,
        color3: colors.color3
      });
    }
    
    
    // Helper function to check if a color is black (or very dark)
    const isBlackColor = (color) => {
      if (!color) return false;
      const normalized = color.toLowerCase();
      if (normalized === '#000000' || normalized === '#000' || normalized === '#090909') return true;
      if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]);
          const g = parseInt(rgbMatch[2]);
          const b = parseInt(rgbMatch[3]);
          if (r < 20 && g < 20 && b < 20) return true;
        }
      }
      return false;
    };
    
    // Helper function to check if a color is white (or very light)
    const isWhiteColor = (color) => {
      if (!color) return false;
      const normalized = color.toLowerCase();
      if (normalized === '#ffffff' || normalized === '#fff') return true;
      if (color.startsWith('rgb')) {
        const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]);
          const g = parseInt(rgbMatch[2]);
          const b = parseInt(rgbMatch[3]);
          if (r > 240 && g > 240 && b > 240) return true;
        }
      }
      return false;
    };
    
    // Update gradient stops (preserve opacity if it exists)
    const updateGradientStops = (gradientId, newColor) => {
      const defsElement = svgElement.querySelector('defs');
      if (!defsElement) {
        return;
      }
      
      // Try different selector methods to find the gradient
      let gradient = defsElement.querySelector(`#${gradientId}`);
      if (!gradient) {
        // Try with escaped ID (in case of special characters)
        gradient = defsElement.querySelector(`[id="${gradientId}"]`);
      }
      if (!gradient) {
        // Try querySelectorAll and find manually
        const allGradients = defsElement.querySelectorAll('linearGradient, radialGradient');
        gradient = Array.from(allGradients).find(g => g.getAttribute('id') === gradientId);
      }
      
      if (!gradient) {
        return;
      }
      
      const stops = gradient.querySelectorAll('stop');
      if (stops.length === 0) {
        return;
      }
      
      // Get opacity values for this gradient (if stored during analysis)
      const opacityValues = gradientOpacityMap?.get(gradientId) || [];
      
      let updatedCount = 0;
      stops.forEach((stop, index) => {
        // Update stop-color attribute
        stop.setAttribute('stop-color', newColor);
        
        // Preserve original opacity if it exists
        const originalOpacity = opacityValues[index] !== undefined ? opacityValues[index] : null;
        
        // Update style attribute
        let styleAttr = stop.getAttribute('style') || '';
        
        // Update stop-color in style
        if (styleAttr.includes('stop-color:')) {
          styleAttr = styleAttr.replace(/stop-color:\s*[^;]+/gi, `stop-color: ${newColor}`);
        } else {
          styleAttr = styleAttr ? `${styleAttr}; stop-color: ${newColor}` : `stop-color: ${newColor}`;
        }
        
        // Preserve or update opacity
        if (originalOpacity !== null && originalOpacity !== 1.0) {
          if (styleAttr.includes('stop-opacity:')) {
            styleAttr = styleAttr.replace(/stop-opacity:\s*[^;]+/gi, `stop-opacity: ${originalOpacity}`);
          } else {
            styleAttr = `${styleAttr}; stop-opacity: ${originalOpacity}`;
          }
          stop.setAttribute('stop-opacity', originalOpacity.toString());
        } else if (stop.getAttribute('stop-opacity')) {
          // Keep existing opacity if no stored value
          const existingOpacity = stop.getAttribute('stop-opacity');
          if (styleAttr.includes('stop-opacity:')) {
            styleAttr = styleAttr.replace(/stop-opacity:\s*[^;]+/gi, `stop-opacity: ${existingOpacity}`);
          } else {
            styleAttr = `${styleAttr}; stop-opacity: ${existingOpacity}`;
          }
        }
        
        stop.setAttribute('style', styleAttr);
        updatedCount++;
      });
      
    };
    
    // Apply colors to elements (including gradients)
    const applyColorToClasses = (classArray, color, colorName = 'unknown') => {
      // Track processed elements to avoid processing the same element multiple times
      // when it has multiple classes from the same color group
      const processedElements = new Set();
      
      // First pass: collect all elements that need to be processed
      const elementsToProcess = new Map(); // element -> { classes: Set, hasOpacity: boolean, opacity: number }
      
      classArray.forEach(className => {
        const elements = svgElement.querySelectorAll(`.${className}`);
        elements.forEach(element => {
          if (!elementsToProcess.has(element)) {
            const elementClasses = (element.className.baseVal || element.className || '').split(/\s+/).filter(c => c);
            const classesSet = new Set(elementClasses);
            
            // Check if ANY class on this element has opacity
            let hasOpacity = false;
            let opacityValue = null;
            for (const cls of elementClasses) {
              if (classOpacityMap?.has(cls)) {
                const clsOpacity = classOpacityMap.get(cls);
                if (clsOpacity !== undefined && clsOpacity !== null) {
                  hasOpacity = true;
                  opacityValue = clsOpacity;
                  break; // Use the first opacity found
                }
              }
            }
            
            elementsToProcess.set(element, {
              classes: classesSet,
              hasOpacity: hasOpacity,
              opacity: opacityValue
            });
          } else {
            // Element already in map, update opacity if this class has it
            // IMPORTANT: Prioritize opacity from classes that have it
            const existing = elementsToProcess.get(element);
            if (classOpacityMap?.has(className)) {
              const clsOpacity = classOpacityMap.get(className);
              if (clsOpacity !== undefined && clsOpacity !== null) {
                existing.hasOpacity = true;
                existing.opacity = clsOpacity; // Always update if this class has opacity
              }
            }
          }
        });
      });
      
      // Second pass: process all elements with correct opacity
      classArray.forEach(className => {
        const gradientId = gradientClassMap?.get(className);
        
        // If this class uses a gradient, update the gradient stops
        if (gradientId) {
          updateGradientStops(gradientId, color);
        }
      });
      
      // Process all collected elements
      elementsToProcess.forEach((data, element) => {
        // Find the opacity value from any class on this element that has opacity
        let elementOpacity = data.opacity;
        
        // Check if element uses a gradient (check all classes)
        let gradientId = null;
        for (const cls of data.classes) {
          const gId = gradientClassMap?.get(cls);
          if (gId) {
            gradientId = gId;
            break;
          }
        }
        
        // For gradients, we don't want to override the fill URL, but we can update style as fallback
        if (!gradientId) {
          element.style.fill = color;
          element.setAttribute('fill', color);
          
          // Apply opacity ONLY if the element has a class with opacity
          // This ensures opacity is only applied to elements that actually have a class with opacity
          if (elementOpacity !== null) {
            // Use !important to ensure opacity is applied even if CSS tries to override it
            // Remove opacity attribute to prevent SVG attribute from conflicting with inline style
            element.style.setProperty('opacity', elementOpacity.toString(), 'important');
            element.removeAttribute('opacity');
          } else {
            // Remove opacity completely if the element doesn't have any class with opacity
            // Explicitly set opacity: 1 inline with !important to override any CSS opacity
            // Remove the opacity attribute entirely to prevent SVG attribute from overriding CSS
            element.style.setProperty('opacity', '1', 'important');
            element.removeAttribute('opacity');
          }
        } else {
          // For gradients, ensure the fill URL is preserved
          const currentFill = element.getAttribute('fill') || element.style.fill;
          if (!currentFill || !currentFill.includes('url')) {
            // If somehow the gradient URL is missing, try to restore it
            element.setAttribute('fill', `url(#${gradientId})`);
          }
          
          // Apply opacity based on element's classes, not just the current class
          if (elementOpacity !== null) {
            // Use !important to ensure opacity is applied even if CSS tries to override it
            // Remove opacity attribute to prevent SVG attribute from conflicting with inline style
            element.style.setProperty('opacity', elementOpacity.toString(), 'important');
            element.removeAttribute('opacity');
          } else {
            // Remove opacity completely if the element doesn't have any class with opacity
            // Explicitly set opacity: 1 inline with !important to override any CSS opacity
            // Remove the opacity attribute entirely to prevent SVG attribute from overriding CSS
            element.style.setProperty('opacity', '1', 'important');
            element.removeAttribute('opacity');
          }
        }
      });
    };
    
    // Update CSS class definitions in the style section FIRST
    // This ensures that when we process elements, the CSS is already correct
    if (styleElement && styleContent) {
      let updatedStyleContent = styleContent;
      
      // Get the original colors for each group to match them in CSS
      // Use the actual colors found in the groups (prioritize original colors)
      const color1OriginalColor = color1Group?.color || mainColorGroups[0]?.color;
      const color2OriginalColor = color2Group?.color || mainColorGroups[1]?.color;
      const color3OriginalColor = color3Group?.color || mainColorGroups[2]?.color;
      const logoOriginalColor = colorGroupsArray.find(g => 
        logoColorPatterns.some(pattern => pattern.test(g.color))
      )?.color;
      const numberOriginalColor = colorGroupsArray.find(g => 
        numberColorPatterns.some(pattern => pattern.test(g.color))
      )?.color;
      
      // Helper function to split grouped selectors
      const splitGroupedSelectors = (selectors, colorGroup, colorValue, originalColor) => {
        // Find all grouped selectors (selectors with commas) that have fill property
        // Match pattern like: ".cls-2, .cls-3 { fill: #070707; }"
        // Updated regex to handle whitespace and newlines more robustly
        // Using multiline flag to handle newlines in selectors
        const groupedRegex = new RegExp(`([^\\n{]+(?:,\\s*[^\\n{]+)+)\\s*\\{[^}]*fill:\\s*[^;\\s}]+[^}]*\\}`, 'gm');
        const processedGroups = new Set();
        
        return updatedStyleContent.replace(groupedRegex, (match, fullMatch) => {
          // Extract selector part and content part
          const braceIndex = match.indexOf('{');
          if (braceIndex === -1) return match;
          
          const selectorPart = match.substring(0, braceIndex).trim();
          const contentPart = match.substring(braceIndex + 1, match.lastIndexOf('}')).trim();
          
          // Check if already processed
          if (processedGroups.has(selectorPart)) {
            return match; // Already processed
          }
          
          // Check if this grouped selector contains any class from our color group
          const selectorList = selectorPart.split(',').map(s => s.trim());
          const hasColorGroupClass = selectorList.some(sel => {
            const clsName = sel.replace(/^\./, '').trim();
            return colorGroup.includes(clsName);
          });
          
          if (!hasColorGroupClass) {
            return match; // Doesn't contain our classes, skip
          }
          
          processedGroups.add(selectorPart);
          
          // Split into individual rules
          let newRules = '';
          selectorList.forEach(sel => {
            const clsName = sel.replace(/^\./, '').trim();
            // CRITICAL: Double-check that this class actually has opacity in the original SVG
            const clsHasOpacity = classOpacityMap?.has(clsName) && 
                                 classOpacityMap?.get(clsName) !== undefined && 
                                 classOpacityMap?.get(clsName) !== null;
            const clsOpacity = clsHasOpacity ? classOpacityMap.get(clsName) : null;
            
            // Debug logging for back SVG
            if (svgType === 'BACK' && (clsName === 'cls-2' || clsName === 'cls-3')) {
              console.log(`[${svgType}] splitGroupedSelectors: ${clsName} - hasOpacity: ${clsHasOpacity}, opacity: ${clsOpacity}`);
            }
            
            if (colorGroup.includes(clsName)) {
              // Update fill and handle opacity for this class
              let newContent = contentPart.replace(/fill:\s*[^;}\s]+/, `fill: ${colorValue}`);
              if (clsHasOpacity && clsOpacity !== null) {
                // Remove any existing opacity first to avoid duplicates
                newContent = newContent.replace(/opacity:\s*[^;}\s]+(!important\s*)*;?\s*/g, '').trim();
                // Add opacity with single !important
                newContent = newContent.trim() + ` opacity: ${clsOpacity} !important;`;
              } else {
                // Remove opacity if this class doesn't have it
                newContent = newContent.replace(/opacity:\s*[^;}\s]+(!important\s*)*;?\s*/g, '').trim();
                // Explicitly add opacity: 1 !important to ensure no opacity is applied
                if (!newContent.includes('opacity:')) {
                  newContent = newContent.trim() + ` opacity: 1 !important;`;
                }
              }
              newRules += `      ${sel} {\n        ${newContent}\n      }\n`;
            } else {
              // Keep original for other classes (not in our color group)
              newRules += `      ${sel} {\n        ${contentPart}\n      }\n`;
            }
          });
          return newRules;
        });
      };
      
      // Update all classes in color1 group
      color1Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const gradientId = gradientClassMap?.get(className);
        
        if (gradientId) {
          // Update gradient stops in style content if referenced
          // This is handled by updateGradientStops, but we can also update CSS if needed
        } else if (color1OriginalColor) {
          // Match any fill value for this class and replace with color1
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*fill:\\s*[^;\\s}]+[^}]*\\}`, 'g'),
            (match) => match.replace(/fill:\s*[^;}\s]+/, `fill: ${colors.color1}`)
          );
        }
      });
      
      // Update all classes in color2 group
      // STEP 1: First, remove ALL opacity rules for classes that don't have opacity
      // This must happen BEFORE we split grouped selectors to prevent incorrect opacity
      color2Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const classHasOpacity = classOpacityMap?.has(className) === true;
        
        if (!classHasOpacity) {
          // Aggressively remove any opacity rules for this class - try ALL possible patterns
          // Pattern 1: Standalone opacity rules
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}\\s*`, 'g'),
            ''
          );
          // Pattern 2: Multi-line opacity rules
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:[^}]*\\}\\s*`, 'gs'),
            ''
          );
          // Pattern 3: Remove opacity from rules with other properties
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`(\\.${escapedClassName}\\s*\\{[^}]*?)(opacity:\\s*[^;\\s}]+(!important\\s*)*;?\\s*)([^}]*?\\})`, 'g'),
            '$1$4'
          );
          // Pattern 4: Catch-all - remove any opacity declaration
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`(\\.${escapedClassName}[^{]*\\{[^}]*?)opacity:\\s*[^;}\\s]+(!important\\s*)*;?\\s*`, 'gi'),
            '$1'
          );
        }
      });
      
      // Debug: Log classOpacityMap for color2 classes
      if (svgType === 'BACK') {
        console.log(`[${svgType}] color2Classes array:`, color2Classes);
        console.log(`[${svgType}] classOpacityMap for color2 classes:`, 
          Array.from(color2Classes).map(cls => ({ 
            class: cls, 
            hasOpacity: classOpacityMap?.has(cls), 
            opacity: classOpacityMap?.get(cls) 
          }))
        );
      }
      
      // STEP 2: Handle grouped selectors by splitting them
      updatedStyleContent = splitGroupedSelectors(color2Classes, color2Classes, colors.color2, color2OriginalColor);
      
      // STEP 3: Then handle individual class rules (non-grouped) and standalone opacity rules
      color2Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const gradientId = gradientClassMap?.get(className);
        // CRITICAL: Only consider class as having opacity if it's actually in classOpacityMap
        // This ensures we don't incorrectly add opacity rules for classes that don't have it
        const classHasOpacity = classOpacityMap?.has(className) === true;
        const classOpacity = classHasOpacity ? classOpacityMap.get(className) : null;
        
        if (!gradientId && color2OriginalColor) {
          // Handle individual (non-grouped) class rules that have fill
          // Skip if it's part of a grouped selector (already handled above)
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`^\\.${escapedClassName}\\s*\\{[^}]*fill:\\s*[^;\\s}]+[^}]*\\}`, 'gm'),
            (match) => {
              // Only process if this is NOT part of a grouped selector (no comma before it)
              if (!match.includes(',')) {
                let updated = match.replace(/fill:\s*[^;}\s]+/, `fill: ${colors.color2}`);
                // CRITICAL: Only add opacity if class actually has it in classOpacityMap
                // Double-check to prevent incorrectly adding opacity
                const reallyHasOpacity = classOpacityMap?.has(className) === true && 
                                        classOpacityMap?.get(className) !== undefined && 
                                        classOpacityMap?.get(className) !== null;
                if (reallyHasOpacity && classOpacity !== null) {
                  // Remove any existing opacity first to avoid duplicates
                  updated = updated.replace(/opacity:\s*[^;}\s]+(!important\s*)*;?\s*/g, '').trim();
                  // Add opacity with single !important
                  updated = updated.replace(/\}/, ` opacity: ${classOpacity} !important; }`);
                } else {
                  // Remove opacity completely if class doesn't have it
                  updated = updated.replace(/opacity:\s*[^;}\s]+(!important\s*)*;?\s*/g, '').trim();
                }
                return updated;
              }
              return match; // Keep grouped selectors as-is (already handled)
            }
          );
          
          // Handle standalone opacity rules (rules that only have opacity, no fill)
          // CRITICAL: Only add/update opacity rules if the class ORIGINALLY had opacity
          // Double-check that classOpacityMap actually has this class with opacity
          const actuallyHasOpacity = classOpacityMap?.has(className) && 
                                     classOpacityMap?.get(className) !== undefined && 
                                     classOpacityMap?.get(className) !== null;
          
          if (actuallyHasOpacity && classOpacity !== null) {
            // CRITICAL: This class SHOULD have opacity - preserve it!
            // Remove ALL existing opacity rules for this class first (to avoid duplicates)
            // But then immediately add the correct opacity rule
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}\\s*`, 'g'),
              ''
            );
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:[^}]*\\}\\s*`, 'gs'),
              ''
            );
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`(\\.${escapedClassName}\\s*\\{[^}]*?)(opacity:\\s*[^;\\s}]+(!important\\s*)*;?\\s*)([^}]*?\\})`, 'g'),
              '$1$4'
            );
            // Now add a single opacity rule with the correct value
            // Use a unique marker to ensure this rule is added and not removed by cleanup
            updatedStyleContent += `\n      .${className} {\n        opacity: ${classOpacity} !important;\n      }`;
          } else {
            // Remove standalone opacity rules for classes that don't have opacity
            // Use a more aggressive regex to catch all variations
            // Try multiple patterns to ensure we catch all cases
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}\\s*`, 'g'),
              ''
            );
            // Also remove any opacity rules that might be on multiple lines
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:[^}]*\\}\\s*`, 'gs'),
              ''
            );
            // Remove opacity from rules that have other properties too
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`(\\.${escapedClassName}\\s*\\{[^}]*?)(opacity:\\s*[^;\\s}]+;?\\s*)([^}]*?\\})`, 'g'),
              '$1$3'
            );
            // Explicitly add opacity: 1 to ensure no opacity is applied from CSS
            // BUT ONLY if this class doesn't have opacity in classOpacityMap
            // CRITICAL: Double-check that this class really doesn't have opacity
            const reallyDoesntHaveOpacity = !classOpacityMap?.has(className) || 
                                            classOpacityMap?.get(className) === undefined || 
                                            classOpacityMap?.get(className) === null;
            
            if (reallyDoesntHaveOpacity) {
              // Check if there's already a rule for this class (might have been created by splitGroupedSelectors)
              const existingRuleRegex = new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*\\}`, 'g');
              if (existingRuleRegex.test(updatedStyleContent)) {
                // Update existing rule to add opacity: 1
                updatedStyleContent = updatedStyleContent.replace(
                  new RegExp(`(\\.${escapedClassName}\\s*\\{[^}]*)(\\})`, 'g'),
                  (match, beforeBrace, closingBrace) => {
                    // Only add opacity if it's not already there AND class doesn't have opacity
                    if (!beforeBrace.includes('opacity:')) {
                      return beforeBrace.trim() + ' opacity: 1 !important;' + closingBrace;
                    }
                    return match;
                  }
                );
              } else {
                // Add new rule with opacity: 1 !important
                updatedStyleContent += `\n      .${className} {\n        opacity: 1 !important;\n      }`;
              }
            }
            // If class HAS opacity, don't add opacity: 1 - it should already be handled by the if block above
          }
        }
      });
      
      // Update all classes in color3 group
      color3Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const gradientId = gradientClassMap?.get(className);
        
        if (!gradientId && color3OriginalColor) {
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*fill:\\s*[^;\\s}]+[^}]*\\}`, 'g'),
            (match) => match.replace(/fill:\s*[^;}\s]+/, `fill: ${colors.color3}`)
          );
        }
      });
      
      // Update logo color classes
      color4Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (logoOriginalColor) {
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*fill:\\s*[^;\\s}]+[^}]*\\}`, 'g'),
            (match) => match.replace(/fill:\s*[^;}\s]+/, `fill: ${colors.logoColor}`)
          );
        }
      });
      
      // Update number color classes
      color5Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (numberOriginalColor) {
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*fill:\\s*[^;\\s}]+[^}]*\\}`, 'g'),
            (match) => match.replace(/fill:\s*[^;}\s]+/, `fill: ${colors.numberColor}`)
          );
        }
      });
      
      // PRE-FINAL CLEANUP: Remove opacity from classes that shouldn't have it
      // This runs BEFORE we add standalone opacity rules to ensure we don't remove correct rules
      color2Classes.forEach(className => {
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const classHasOpacity = classOpacityMap?.has(className) === true && 
                               classOpacityMap?.get(className) !== undefined && 
                               classOpacityMap?.get(className) !== null;
        
        // Debug logging for back SVG
        if (svgType === 'BACK' && (className === 'cls-2' || className === 'cls-3')) {
          console.log(`[${svgType}] PRE-FINAL CLEANUP: ${className} - hasOpacity: ${classHasOpacity}`);
        }
        
        if (!classHasOpacity) {
          // Remove any existing opacity rules for classes without opacity
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}\\s*`, 'g'),
            ''
          );
          updatedStyleContent = updatedStyleContent.replace(
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:[^}]*\\}\\s*`, 'gs'),
            ''
          );
        }
      });
      
      // FINAL CLEANUP: Remove any remaining opacity rules for classes that shouldn't have opacity
      // This is a safety net to catch any opacity rules that might have been missed
      // BUT: This runs AFTER we've added opacity rules, so we must be extra careful
      // We do this multiple times to ensure we catch all variations
      for (let cleanupPass = 0; cleanupPass < 3; cleanupPass++) {
        color2Classes.forEach(className => {
          const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // CRITICAL: Triple-check that this class doesn't have opacity
          // Only remove opacity if classOpacityMap confirms it doesn't have opacity
          const classHasOpacity = classOpacityMap?.has(className) === true && 
                                 classOpacityMap?.get(className) !== undefined && 
                                 classOpacityMap?.get(className) !== null;
          
          // Debug logging for back SVG (only on first pass to avoid spam)
          if (cleanupPass === 0 && svgType === 'BACK' && (className === 'cls-2' || className === 'cls-3')) {
            console.log(`[${svgType}] FINAL CLEANUP pass ${cleanupPass}: ${className} - hasOpacity: ${classHasOpacity}`);
          }
          
          // ONLY remove opacity if class truly doesn't have it
          if (!classHasOpacity) {
            // Final aggressive removal - remove ALL opacity rules for this class
            // Pattern 1: Standalone opacity rules (single line)
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}\\s*`, 'g'),
              ''
            );
            // Pattern 2: Multi-line opacity rules
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:[^}]*\\}\\s*`, 'gs'),
              ''
            );
            // Pattern 3: Remove opacity from within rules (with other properties)
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`(\\.${escapedClassName}[^{]*\\{[^}]*?)opacity:\\s*[^;}\\s]+(!important\\s*)*;?\\s*`, 'gi'),
              '$1'
            );
            // Pattern 4: Remove opacity from rules with other properties (more specific)
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`(\\.${escapedClassName}\\s*\\{[^}]*?)(opacity:\\s*[^;\\s}]+(!important\\s*)*;?\\s*)([^}]*?\\})`, 'g'),
              '$1$4'
            );
            // Pattern 5: Remove any opacity declaration, even if it's part of a larger rule
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`(\\.${escapedClassName}[^}]*\\{[^}]*?)opacity:\\s*[0-9.]+(!important)?;?`, 'gi'),
              '$1'
            );
          }
        });
      }
      
      // ULTRA-FINAL VERIFICATION: Scan the entire CSS string for any remaining opacity rules
      // for classes that shouldn't have opacity, and remove them using a simple string search
      color2Classes.forEach(className => {
        // CRITICAL: Triple-check that this class doesn't have opacity
        // Only remove opacity if classOpacityMap confirms it doesn't have opacity
        const classHasOpacity = classOpacityMap?.has(className) === true && 
                                classOpacityMap?.get(className) !== undefined && 
                                classOpacityMap?.get(className) !== null;
        
        // Only proceed with removal if class truly doesn't have opacity
        if (!classHasOpacity) {
          // Use a very simple pattern: find any rule that starts with .className and contains opacity
          // This catches any variation we might have missed
          const simplePattern = new RegExp(`\\.${className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^{]*\\{[^}]*opacity:[^}]*\\}`, 'gi');
          let match;
          while ((match = simplePattern.exec(updatedStyleContent)) !== null) {
            // Remove this entire rule
            updatedStyleContent = updatedStyleContent.replace(match[0], '');
            // Reset regex lastIndex to avoid infinite loop
            simplePattern.lastIndex = 0;
          }
          
          // Also remove opacity from rules that have other properties
          const opacityInRulePattern = new RegExp(`(\\.${className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^{]*\\{[^}]*?)opacity:\\s*[^;}\\s]+[^}]*?([^}]*\\})`, 'gi');
          updatedStyleContent = updatedStyleContent.replace(opacityInRulePattern, '$1$2');
          
          // NUCLEAR OPTION: Split CSS into lines and remove any line containing both className and opacity
          // BUT ONLY if the class shouldn't have opacity
          const lines = updatedStyleContent.split('\n');
          const cleanedLines = lines.filter(line => {
            // If this line contains the class name and opacity, and the class shouldn't have opacity, remove it
            const hasClassName = line.includes(`.${className}`) || new RegExp(`\\.${className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(line);
            const hasOpacity = /opacity:\s*[^;}\s]+/.test(line);
            // CRITICAL: Only remove if this class should NOT have opacity
            if (hasClassName && hasOpacity && !classHasOpacity) {
              // Check if this is a standalone opacity rule (only has opacity, no fill or other properties)
              const isStandaloneOpacityRule = new RegExp(`\\.${className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\{\\s*opacity:\\s*[^}]+\\s*\\}`).test(line.trim());
              if (isStandaloneOpacityRule) {
                return false; // Remove this line
              }
            }
            return true; // Keep all other lines (including lines for classes that SHOULD have opacity)
          });
          updatedStyleContent = cleanedLines.join('\n');
          
          // Remove opacity from any remaining rules that contain both className and opacity
          // BUT ONLY if the class shouldn't have opacity
          if (!classHasOpacity) {
            updatedStyleContent = updatedStyleContent.replace(
              new RegExp(`(\\.${className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^{]*\\{[^}]*?)opacity:\\s*[^;}\\s]+(!important)?;?\\s*`, 'gi'),
              '$1'
            );
          }
        }
      });
      
      // POST-CLEANUP VERIFICATION: Ensure classes that SHOULD have opacity still have it
      // This is a final safeguard to restore opacity if cleanup accidentally removed it
      color2Classes.forEach(className => {
        const classHasOpacity = classOpacityMap?.has(className) === true && 
                               classOpacityMap?.get(className) !== undefined && 
                               classOpacityMap?.get(className) !== null;
        const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Debug logging for back SVG
        if (svgType === 'BACK' && (className === 'cls-2' || className === 'cls-3')) {
          console.log(`[${svgType}] POST-CLEANUP VERIFICATION: ${className} - hasOpacity: ${classHasOpacity}`);
        }
        
        if (classHasOpacity) {
          const classOpacity = classOpacityMap.get(className);
          
          // Check if opacity rule exists for this class
          const opacityRuleExists = new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}`, 'i').test(updatedStyleContent);
          
          // Debug logging for back SVG
          if (svgType === 'BACK' && (className === 'cls-2' || className === 'cls-3')) {
            console.log(`[${svgType}] POST-CLEANUP: ${className} - opacityRuleExists: ${opacityRuleExists}, will ${opacityRuleExists ? 'keep' : 'add'} opacity: ${classOpacity}`);
          }
          
          // If opacity rule doesn't exist, add it
          if (!opacityRuleExists) {
            updatedStyleContent += `\n      .${className} {\n        opacity: ${classOpacity} !important;\n      }`;
          }
        } else {
          // CRITICAL: This class should NOT have opacity - do a final aggressive removal
          // Remove ALL opacity rules for this class, including any that might have been added
          // Use multiple regex patterns to catch all variations
          const patterns = [
            // Pattern 1: Standalone opacity rule
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:\\s*[^;\\s}]+[^}]*\\}\\s*`, 'gi'),
            // Pattern 2: Multi-line opacity rule
            new RegExp(`\\.${escapedClassName}\\s*\\{[^}]*opacity:[^}]*\\}\\s*`, 'gis'),
            // Pattern 3: Opacity within rule with other properties
            new RegExp(`(\\.${escapedClassName}[^{]*\\{[^}]*?)opacity:\\s*[^;}\\s]+(!important\\s*)*;?\\s*`, 'gi'),
            // Pattern 4: Opacity in rule with other properties (more specific)
            new RegExp(`(\\.${escapedClassName}\\s*\\{[^}]*?)(opacity:\\s*[^;\\s}]+(!important\\s*)*;?\\s*)([^}]*?\\})`, 'g'),
            // Pattern 5: Any opacity declaration
            new RegExp(`(\\.${escapedClassName}[^}]*\\{[^}]*?)opacity:\\s*[0-9.]+(!important)?;?`, 'gi'),
          ];
          
          // Apply each pattern multiple times to catch all variations
          for (let i = 0; i < 3; i++) {
            patterns.forEach(pattern => {
              updatedStyleContent = updatedStyleContent.replace(pattern, (match, p1, p2, p3, p4) => {
                // If pattern has capture groups, return the parts without opacity
                if (p1 !== undefined && p4 !== undefined) {
                  return p1 + p4; // Remove opacity, keep rest
                } else if (p1 !== undefined) {
                  return p1; // Remove opacity
                }
                return ''; // Remove entire rule
              });
            });
          }
        }
      });
      
      styleElement.textContent = updatedStyleContent;
      
      // Force a reflow to ensure CSS is applied before processing elements
      // This ensures that when we query elements, they have the updated CSS
      void svgElement.offsetHeight; // Force reflow
    }
    
    // Apply colors based on the mapping AFTER CSS is updated
    // This ensures elements use the updated CSS rules
    applyColorToClasses(color1Classes, colors.color1, 'color1');
    applyColorToClasses(color2Classes, colors.color2, 'color2');
    applyColorToClasses(color3Classes, colors.color3, 'color3');
    applyColorToClasses(color4Classes, colors.logoColor, 'logoColor');
    applyColorToClasses(color5Classes, colors.numberColor, 'numberColor');
    
    // Fix dark overlay: Apply mix-blend-mode: screen to images with HIGHLIGHTS in data-name
    // This fixes the dark issue for all categories
    if (svgType === 'FRONT') {
      const allImages = svgElement.querySelectorAll('image');
      let highlightsFixed = 0;
      
      allImages.forEach((img) => {
        const dataName = img.getAttribute('data-name') || '';
        const id = img.getAttribute('id') || '';
        
        // Check if this image contains "HIGHLIGHTS" in data-name or id
        if (dataName.toUpperCase().includes('HIGHLIGHT') || id.toUpperCase().includes('HIGHLIGHT')) {
          // Apply mix-blend-mode: screen via inline style
          let currentStyle = img.getAttribute('style') || '';
          
          // Remove any existing mix-blend-mode
          currentStyle = currentStyle.replace(/mix-blend-mode:\s*[^;]+;?/gi, '').trim();
          
          // Add mix-blend-mode: screen
          if (currentStyle) {
            currentStyle += '; mix-blend-mode: screen;';
          } else {
            currentStyle = 'mix-blend-mode: screen;';
          }
          
          img.setAttribute('style', currentStyle);
          highlightsFixed++;
        }
      });
      
      if (highlightsFixed > 0) {
        console.log(`[${svgType}] Applied mix-blend-mode: screen to ${highlightsFixed} HIGHLIGHTS image(s) to fix dark overlay (category: ${category})`);
      }
    }
    
    // Return the modified SVG as HTML string
    const tempDiv = document.createElement('div');
    const clonedSvg = svgElement.cloneNode(true);
    
    // Apply same mix-blend-mode fix to cloned SVG
    if (svgType === 'FRONT') {
      const clonedImages = clonedSvg.querySelectorAll('image');
      clonedImages.forEach((img) => {
        const dataName = img.getAttribute('data-name') || '';
        const id = img.getAttribute('id') || '';
        
        // Check if this image contains "HIGHLIGHTS" in data-name or id
        if (dataName.toUpperCase().includes('HIGHLIGHT') || id.toUpperCase().includes('HIGHLIGHT')) {
          // Apply mix-blend-mode: screen via inline style
          let currentStyle = img.getAttribute('style') || '';
          
          // Remove any existing mix-blend-mode
          currentStyle = currentStyle.replace(/mix-blend-mode:\s*[^;]+;?/gi, '').trim();
          
          // Add mix-blend-mode: screen
          if (currentStyle) {
            currentStyle += '; mix-blend-mode: screen;';
          } else {
            currentStyle = 'mix-blend-mode: screen;';
          }
          
          img.setAttribute('style', currentStyle);
        }
      });
    }
    
    tempDiv.appendChild(clonedSvg);
    return tempDiv.innerHTML;
  };
  
  // Main function to modify SVG colors - now uses two-phase approach
  const modifySvgColors = (svgContent, colors, svgType = 'unknown', category = 'football') => {
    if (!svgContent) return null;
    
    
    // PHASE 1: Analyze SVG first (without applying colors)
    const analysisResult = analyzeSvgColors(svgContent, svgType);
    
    if (!analysisResult) {
      return svgContent;
    }
    
    // PHASE 2: Apply colors based on analysis
    const modifiedSvg = applyColorsToSvg(analysisResult, colors, svgType, category);
    
    if (!modifiedSvg) {
      return svgContent;
    }
    
    
    return modifiedSvg;
  };

  // Update SVG colors when selectedColors or SVG data changes
  useEffect(() => {
    // Check if design is full-body based on front SVG
    if (frontSvgData) {
      const fullBody = checkIfFullBody(frontSvgData);
      setIsFullBodyDesign(fullBody);
    } else if (backSvgData) {
      const fullBody = checkIfFullBody(backSvgData);
      setIsFullBodyDesign(fullBody);
    } else {
      setIsFullBodyDesign(true); // Default to full body
    }
    
    // Test with a simple SVG if no data is available
    if (!frontSvgData && !backSvgData) {
      const testSvg = `
        <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="100" r="50" fill="#ffffff" stroke="#000000" stroke-width="2"/>
          <rect x="50" y="50" width="100" height="100" fill="#ff0000"/>
          <polygon points="100,20 120,60 80,60" fill="#ff6500"/>
          <ellipse cx="100" cy="150" rx="30" ry="20" fill="#090909"/>
          <path d="M20,100 Q100,50 180,100" stroke="#fff" stroke-width="3" fill="none"/>
        </svg>
      `;
      const modifiedTest = modifySvgColors(testSvg, selectedColors, 'FRONT', category);
      setModifiedFrontSvg(modifiedTest);
    }
    
    if (frontSvgData) {
      const modified = modifySvgColors(frontSvgData, selectedColors, 'FRONT', category);
      setModifiedFrontSvg(modified);
    }
    if (backSvgData) {
      const modified = modifySvgColors(backSvgData, selectedColors, 'BACK', category);
      setModifiedBackSvg(modified);
    } else {
      setModifiedBackSvg(null);
    }
  }, [frontSvgData, backSvgData, selectedColors, category]);

  // Create a dynamic SVG that changes colors based on selection
  const generateKitSVG = (view = 'front') => {
    return `
      <svg width="400" height="500" viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg">
        <!-- Kit Base -->
        <defs>
          <pattern id="kitPattern" patternUnits="userSpaceOnUse" width="100" height="100">
            <rect width="100" height="100" fill="${selectedColors.color1}"/>
            <polygon points="0,0 50,25 100,0 75,50 100,100 50,75 0,100 25,50" fill="${selectedColors.color2}" opacity="0.3"/>
          </pattern>
        </defs>
        
        <!-- Jersey -->
        <g transform="translate(150, 50)">
          <!-- Jersey Base -->
          <path d="M50 0 L50 20 L45 25 L45 35 L55 35 L55 25 L50 20 Z" fill="${selectedColors.color1}"/>
          <path d="M45 25 L45 35 L55 35 L55 25 L50 20 Z" fill="${selectedColors.color2}"/>
          
          <!-- Collar -->
          <path d="M45 15 L50 10 L55 15 L55 20 L45 20 Z" fill="${selectedColors.color3}"/>
          
          <!-- Sleeves -->
          <path d="M30 25 L45 25 L45 35 L30 35 Z" fill="${selectedColors.color1}"/>
          <path d="M55 25 L70 25 L70 35 L55 35 Z" fill="${selectedColors.color1}"/>
          
          <!-- Logo Area -->
          <circle cx="50" cy="30" r="8" fill="${selectedColors.color4}" opacity="0.8"/>
          <text x="50" y="32" text-anchor="middle" font-size="4" fill="white" font-weight="bold">SZ</text>
          <text x="50" y="36" text-anchor="middle" font-size="4" fill="white" font-weight="bold">ZAROR</text>
          
          <!-- Number (Back View) -->
          ${view === 'back' ? `
            <text x="50" y="45" text-anchor="middle" font-size="20" fill="${selectedColors.color5}" font-weight="bold">10</text>
          ` : ''}
        </g>
        
        <!-- Shorts -->
        <g transform="translate(150, 200)">
          <rect x="40" y="0" width="20" height="80" fill="${selectedColors.color1}"/>
        </g>
      </svg>
    `;
  };

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        position: 'relative',
        backgroundColor: 'transparent'
      }}
    >
      {/* Front View */}
      <Box
        sx={{
          width: { xs: '50%', md: '50%' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}
      >
        {svgLoading ? (
          <Box sx={{ textAlign: 'center', color: '#666', p: 3 }}>
            Loading design...
          </Box>
        ) : modifiedFrontSvg ? (
          <Box
            id="kit-front-holder"
            dangerouslySetInnerHTML={{ __html: modifiedFrontSvg }}
            sx={{
              position: 'relative',
              zIndex: 2,
              backgroundColor: 'transparent',
              '& svg': {
                width: '100%',
                maxWidth: { 
                  xs: '200px', 
                  md: isFullBodyDesign ? '400px' : '280px' 
                },
                height: 'auto',
                maxHeight: { 
                  xs: '400px', 
                  md: isFullBodyDesign ? '500px' : '350px' 
                },
                objectFit: 'contain',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
                backgroundColor: 'transparent'
              }
            }}
          />
        ) : frontSvgData === null && backSvgData === null ? (
          <Box sx={{ textAlign: 'center', color: '#666', p: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              SVG design not available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please select a design with SVG files
            </Typography>
          </Box>
        ) : (
          <Box
            dangerouslySetInnerHTML={{ __html: generateKitSVG('front') }}
            sx={{
              position: 'relative',
              zIndex: 2,
              backgroundColor: 'transparent',
              '& svg': {
                width: '100%',
                maxWidth: { 
                  xs: '200px', 
                  md: isFullBodyDesign ? '400px' : '280px' 
                },
                height: 'auto',
                maxHeight: { 
                  xs: '400px', 
                  md: isFullBodyDesign ? '500px' : '350px' 
                },
                objectFit: 'contain',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
                backgroundColor: 'transparent'
              }
            }}
          />
        )}
      </Box>
      
      {/* Back View */}
      <Box
        sx={{
          width: { xs: '50%', md: '50%' },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}
      >
        {svgLoading ? (
          <Box sx={{ textAlign: 'center', color: '#666', p: 3 }}>
            Loading design...
          </Box>
        ) : modifiedBackSvg ? (
          <Box
            id="kit-back-holder"
            dangerouslySetInnerHTML={{ __html: modifiedBackSvg }}
            sx={{
              position: 'relative',
              zIndex: 2,
              backgroundColor: 'transparent',
              '& svg': {
                width: '100%',
                maxWidth: { 
                  xs: '200px', 
                  md: isFullBodyDesign ? '400px' : '280px' 
                },
                height: 'auto',
                maxHeight: { 
                  xs: '400px', 
                  md: isFullBodyDesign ? '500px' : '350px' 
                },
                objectFit: 'contain',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
                backgroundColor: 'transparent'
              }
            }}
          />
        ) : frontSvgData === null && backSvgData === null ? (
          <Box sx={{ textAlign: 'center', color: '#666', p: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              SVG design not available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please select a design with SVG files
            </Typography>
          </Box>
        ) : (
          <Box
            dangerouslySetInnerHTML={{ __html: generateKitSVG('back') }}
            sx={{
              position: 'relative',
              zIndex: 2,
              backgroundColor: 'transparent',
              '& svg': {
                width: '100%',
                maxWidth: { 
                  xs: '200px', 
                  md: isFullBodyDesign ? '400px' : '280px' 
                },
                height: 'auto',
                maxHeight: { 
                  xs: '400px', 
                  md: isFullBodyDesign ? '500px' : '350px' 
                },
                objectFit: 'contain',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
                backgroundColor: 'transparent'
              }
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default KitPreview;
